import {
  emptyPvpCardSlots,
  type PvpAppearance,
  type PvpCloseReason,
  type PvpTeamBlastFrame,
  type PvpTeamBleedBurstFrame,
  type PvpTeamBleedFrame,
  type PvpTeamEndedFrame,
  type PvpTeamMissileFrame,
  type PvpTeamParticipantFrame,
  type PvpTeamPoisonFrame,
  type PvpTeamProcFrame,
  type PvpTeamProjectileFrame,
  type PvpTeamReflectFrame,
  type PvpTeamRosterEntry,
  type PvpTeamSelf,
  type PvpTeamStateFrame,
  type PvpTeamWelcomeFrame,
  pvpTeamServerFrameSchema,
} from '@kenocyber/shared';
import type {
  PvpArena,
  PvpClientFailure,
  PvpClientState,
  PvpIntent,
  PvpSocket,
} from './connection';
import { pvpProcDedupKey } from './proc-dedup';
import type {
  PvpParticipantBleedBurst,
  PvpParticipantBleedFrame,
  PvpParticipantFrame,
  PvpParticipantMissileFrame,
  PvpParticipantPoisonFrame,
  PvpParticipantProjectileFrame,
  PvpParticipantReflect,
  PvpRenderEffects,
  PvpRenderRune,
} from './view';

/**
 * The `team_3v3` socket, as the browser sees it.
 *
 * ## What this module is
 *
 * An ADAPTER, and the reason the 3x3 needed no new renderer. `PvpScene` already
 * draws a list of PARTICIPANTS with an authoritative team on each - that
 * widening shipped with the duel and was measured with six ships - so the only
 * thing missing was somebody to turn the team wire into the shape it already
 * reads. That is this file, and it is why nothing below decides anything: it
 * translates, and the translation is total.
 *
 * ## The rule it inherits, unchanged
 *
 * **The server decides; this file believes it.** No damage, no kill, no winner,
 * no clock. What arrives is applied whole; what does not arrive is not guessed
 * at. Everything `connection.ts` says about the duel's socket is true here, word
 * for word, and the two are separate objects rather than one with a branch
 * because the two protocols are separate unions - a decoder that chose between
 * them at runtime would be one decoder making the dangerous choice quietly.
 *
 * ## What the team wire adds, and what this does with it
 *
 * | Field | Here |
 * | --- | --- |
 * | `you.p` / `you.tm` / `you.sl` | {@link PvpTeamClientState.you}, and the id `pvp:open` carries |
 * | `parts[].tm` | `ships[].team`, so the renderer's roles are the authority's |
 * | `parts[].ap` | `arena.appearance[p]`, filled as chairs are admitted |
 * | `proj[].tm` | `projectiles[].team` |
 * | `winnerTeam` | {@link PvpTeamClientState.result}, which is NOT a `PvpEndedFrame` |
 *
 * Two fields are deliberately DROPPED, and neither is a loss:
 *
 * - `parts[].mx/my`, the movement intent per axis. The duel derives a hull's
 *   heading from where it MOVED between two authoritative samples
 *   (`pvpHullTargetFromMotion`), and using the intent instead would be a second
 *   rule for the same picture that only one of the two modes followed.
 * - `parts[].alive`. The room sends it rather than letting a client infer it,
 *   and no drawn thing consumes it: a hull's health bar comes from `hp`, and
 *   `hp` and `alive` are the same fact from the same tick. Carrying it would
 *   mean a new field on `PvpRenderShip` that nothing reads.
 *
 * ## `correction` is derived here, exactly as the duel derives it
 *
 * It is not a field of either protocol. A welcome is a discontinuity by
 * definition and raises it; a tick that went BACKWARDS is a rebuild and raises
 * it. The renderer drops its interpolation across the seam either way, and no
 * message on the wire had to grow to say so.
 */

/* -------------------------------------------------------------------------- */
/*  What the caller sees                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Everything a team client holds, with the world in the shape the scene reads.
 *
 * ## Why the world is NESTED rather than flattened
 *
 * `world` is a {@link PvpClientState} - the exact value `pvp:authoritative`
 * carries - so the bridge, the view buffer and `PvpScene` are handed the same
 * object the duel hands them, with no cast and no second message type. Copying
 * its fields up to this level would be two places holding one truth, and the
 * first frame they disagreed on would be a ship drawn from one and a phase read
 * from the other.
 *
 * Two of that value's fields are permanently `null` on this path and it is worth
 * saying why rather than leaving somebody to wonder:
 *
 * - `world.seat` is a `PvpSeat`, which a team participant does not have. The
 *   identity lives in {@link you}, and the renderer takes it from `pvp:open`.
 * - `world.result` is a `PvpEndedFrame`, which carries `winnerSeat`. A team
 *   ending names a TEAM, so it lives in {@link result} and is read by
 *   `pvpTeamResultText`. Widening the duel's frame would have widened the
 *   duel's RESULT, which is a rule rather than a picture.
 */
export interface PvpTeamClientState {
  /** The authoritative world, in the duel's own shape. Never partially copied. */
  readonly world: PvpClientState;
  /** Who this browser is, as the room named it. Never a list to choose from. */
  readonly you: PvpTeamSelf | null;
  /** All six chairs as of the last welcome: connected, ready, and their art. */
  readonly roster: readonly PvpTeamRosterEntry[];
  /** Only ever set by an `ended` frame, and it names a TEAM. */
  readonly result: PvpTeamEndedFrame | null;
}

export interface PvpTeamConnectionDeps {
  /**
   * Mints a ONE-USE capability for the caller's own frozen chair.
   *
   * No argument, because there is nothing to name: the chair comes from the
   * database keyed by the session. Called again for every reconnection - the
   * previous one is spent and the room refuses it by design.
   */
  readonly mintTicket: () => Promise<{ ticket: string }>;
  readonly socketUrl: (ticket: string) => string;
  readonly openSocket: (url: string) => PvpSocket;
  readonly now: () => number;
  /** Injected so a test can move twelve seconds without waiting for them. */
  readonly schedule: (run: () => void, ms: number) => () => void;
  /** The rules version this bundle was built against. */
  readonly rulesVersion: number;
}

/** How long to wait before trying the socket again after an unexpected drop. */
const RECONNECT_DELAY_MS = 500;

const EMPTY_WORLD: PvpClientState = {
  phase: 'idle',
  seat: null,
  matchId: null,
  arena: null,
  matchState: null,
  tick: 0,
  ships: [],
  projectiles: [],
  missiles: [],
  effects: [],
  runes: [],
  poison: [],
  bleeding: [],
  msLeft: 0,
  ack: 0,
  correction: 0,
  result: null,
  failure: null,
  /* Três espaços vazios: este fixture não é sobre cartas (V28H.J). */
  cards: emptyPvpCardSlots(),
  failureDetail: null,
};

const EMPTY: PvpTeamClientState = {
  world: EMPTY_WORLD,
  you: null,
  roster: [],
  result: null,
};

/* -------------------------------------------------------------------------- */
/*  The translation, as pure functions                                        */
/* -------------------------------------------------------------------------- */

/**
 * One participant frame as a ship the renderer follows.
 *
 * `team` is the authority's `tm` and never derived from the id: a renderer that
 * read the first character of `a2` would be inventing an alliance, and the day
 * chairs are named anything else it would invent a wrong one.
 */
export function pvpTeamShip(part: PvpTeamParticipantFrame): PvpParticipantFrame {
  return {
    s: part.p,
    team: part.tm,
    x: part.x,
    y: part.y,
    ax: part.ax,
    ay: part.ay,
    hp: part.hp,
    c: part.c,
    /*
     * As SERRAS, quando este casco as tem (gate V28G.P).
     *
     * Copiadas em vez de omitidas porque a cena do 3x3 é a MESMA do duelo: se
     * elas parassem aqui, quatro lâminas desapareceriam ao passar de uma sala de
     * dois para uma de seis, e a única diferença seria este mapeamento.
     *
     * Ausentes quando não há nenhuma, e a ausência atravessa: um `0` explícito
     * faria a cena distinguir "sem serras" de "sem serras" duas vezes.
     */
    ...(part.sw === undefined ? {} : { sw: part.sw }),
    ...(part.sf === undefined ? {} : { sf: part.sf }),
    /*
     * E os dois canhões temporários (gate V28H.J).
     *
     * Faltava, e a falta era invisível de um lado só: os tiros extra chegavam
     * - eles são projéteis como os outros - e a ARMA que os disparava não, o
     * que dava exactamente a arma invisível que este campo existe para evitar.
     */
    ...(part.sc === undefined ? {} : { sc: part.sc }),
    /* E as bandeiras das cartas, pelo mesmo caminho (gates V28H.L e V28H.W). */
    ...(part.fz === undefined ? {} : { fz: part.fz }),
    ...(part.bt === undefined ? {} : { bt: part.bt }),
    /*
     * Os quatro do gelo e do Malware. Escritos um a um como os de cima, e é a
     * armadilha que este ficheiro já pagou quatro vezes: uma tradução à mão
     * entre o frame e a cena deixa cair um campo em silêncio, e o compilador
     * não se queixa porque copiar menos é sempre válido.
     */
    ...(part.mw === undefined ? {} : { mw: part.mw }),
    ...(part.pz === undefined ? {} : { pz: part.pz }),
    ...(part.lt === undefined ? {} : { lt: part.lt }),
    ...(part.ft === undefined ? {} : { ft: part.ft }),
    ...(part.cc === undefined ? {} : { cc: part.cc }),
    ...(part.sk === undefined ? {} : { sk: part.sk }),
    /*
     * **E o que as cartas DESENHAM** (gate V28H.S).
     *
     * Faltava, e a falta era invisível exactamente como a dos canhões descrita
     * acima - a mesma armadilha, no mesmo sítio, três gates depois. No 3x3 o
     * dono ligou as três cartas e não viu efeito nenhum: o círculo mágico, a
     * serra, a carga e o feixe param todos aqui se estas três linhas não
     * existirem, porque a cena do 3x3 é a MESMA do duelo e lê os mesmos campos.
     *
     * Só os TIROS do drone apareciam - eles são projéteis, e os projéteis têm a
     * sua própria lista.
     */
    ...(part.cv === undefined ? {} : { cv: part.cv }),
    ...(part.cg === undefined ? {} : { cg: part.cg }),
    ...(part.cw === undefined ? {} : { cw: part.cw }),
  };
}

/** One projectile, owned by an identity and carrying its owner's team. */
export function pvpTeamProjectile(
  projectile: PvpTeamProjectileFrame,
): PvpParticipantProjectileFrame {
  return {
    i: projectile.i,
    x: projectile.x,
    y: projectile.y,
    s: projectile.p,
    team: projectile.tm,
    /*
     * **E as bandeiras do projétil atravessam** (gate V28H.U).
     *
     * `b` não atravessava, e era um defeito: o 3x3 recebia a Bomba Atômica
     * marcada e desenhava-a como um tiro comum, porque esta tradução escrita à
     * mão a deixava cair em silêncio. É o mesmo modo de falhar que já apanhou
     * `cv`, `cg`, `cw` e a lista de invocações, e a lição é a mesma: um campo
     * novo no fio não chega sozinho ao desenho.
     */
    ...(projectile.b === undefined ? {} : { b: projectile.b }),
    ...(projectile.cb === undefined ? {} : { cb: projectile.cb }),
    ...(projectile.sv === undefined ? {} : { sv: projectile.sv }),
    ...(projectile.br === undefined ? {} : { br: projectile.br }),
  };
}

/**
 * Um míssil, com a identidade do dono e a equipa dele ao lado (gate V28G.L).
 *
 * A direção atravessa tal e qual: ela é autoritativa, e a cena aponta o bico com
 * ela em vez de a deduzir entre dois quadros interpolados.
 */
/**
 * Um stack de veneno, do vocabulário do 3x3 para o do cliente (gate V28G.M).
 *
 * Uma tradução de nomes e mais nada - os três campos são os mesmos nos dois
 * lados. Existe pela mesma razão que {@link pvpTeamMissile} existe: o cliente
 * tem UMA forma para os dois protocolos, e é aqui que o vocabulário do 3x3 entra
 * nela.
 */
export function pvpTeamPoison(stack: PvpTeamPoisonFrame): PvpParticipantPoisonFrame {
  return { i: stack.i, q: stack.q, n: stack.n };
}

/**
 * Uma marca de sangramento, do vocabulário do 3x3 para o do cliente (V28G.N).
 *
 * Uma tradução de nomes e mais nada - os três campos são os mesmos nos dois
 * lados - e existe pela mesma razão que {@link pvpTeamPoison} existe.
 */
export function pvpTeamBleed(mark: PvpTeamBleedFrame): PvpParticipantBleedFrame {
  return { i: mark.i, p: mark.p, q: mark.q };
}

export function pvpTeamMissile(missile: PvpTeamMissileFrame): PvpParticipantMissileFrame {
  return {
    i: missile.i,
    x: missile.x,
    y: missile.y,
    dx: missile.dx,
    dy: missile.dy,
    s: missile.p,
    team: missile.tm,
  };
}

/**
 * One participant's effects.
 *
 * `mt` is the whole reason this is not the duel's code path. With one opponent a
 * client could work out who the illusions walk at; with three a side it is a
 * CHOICE, and a choice each browser made for itself would put the same illusion
 * in two places. The room decides and this carries it - and when the room sends
 * `null` the field stays absent, so the scene draws no illusions rather than
 * picking somebody.
 */
export function pvpTeamEffects(part: PvpTeamParticipantFrame): PvpRenderEffects {
  return {
    seat: part.p,
    doubleDamageTicks: part.e.dd,
    speedTicks: part.e.sp,
    teleportCharges: part.e.tp,
  };
}

/** Every pad and what is on it. Where a pad IS comes from a shared constant. */
export function pvpTeamRunes(runes: PvpTeamStateFrame['runes']): readonly PvpRenderRune[] {
  return runes.map((rune) => ({ pad: rune.p, kind: rune.k }));
}

/**
 * What each chair looks like, as the SERVER decided, keyed by participant id.
 *
 * ## Why this record is filled in place rather than rebuilt
 *
 * `PvpScene` binds the arena ONCE, on the first authoritative message that
 * carries one, and reads `arena.appearance[id]` on every frame afterwards. A
 * chair's art is `null` until its holder has presented a capability, and the
 * room admits the six of them one at a time - so a record copied at bind time
 * could be missing somebody who arrived a moment later, and there is no message
 * on this bus that could correct it.
 *
 * So the connection OWNS one record for the life of its socket and fills it as
 * the answers arrive, from the welcome's roster and from every `parts[].ap`.
 * Its identity is stable, which is precisely what makes the scene's single read
 * correct. This function is the only writer.
 *
 * It never INVENTS one. A chair the server has not described stays `null`, and
 * what to draw for a hull nobody has named is the renderer's decision, made once
 * in `PvpScene` under ADR 0033 - not something an adapter quietly fills in.
 */
export function pvpTeamFillAppearance(
  into: Record<string, PvpAppearance | null>,
  entries: readonly { readonly p: string; readonly ap: PvpAppearance | null }[],
): void {
  for (const entry of entries) {
    if (entry.ap !== null) {
      into[entry.p] = entry.ap;
      continue;
    }
    // Recorded as "not described yet" rather than left absent, so the record
    // says the same thing whether a chair has been seen and has no art or has
    // not been seen at all. Never overwrites art that already arrived.
    into[entry.p] ??= null;
  }
}

/**
 * E os NOMES, pela mesma regra (gate V28H.R).
 *
 * A par de {@link pvpTeamFillAppearance} e não dentro dela: as duas coisas
 * chegam na mesma lista, mas uma é arte e a outra é identidade, e juntá-las
 * numa função faria a próxima passar a ter três responsabilidades.
 *
 * Nunca apaga um nome que já chegou: uma entrada de roster sem nome descreve
 * uma cadeira que ainda não foi ocupada, e não uma pessoa que perdeu o dela.
 */
export function pvpTeamFillNicknames(
  into: Record<string, string | null>,
  entries: readonly { readonly p: string; readonly n?: string | null }[],
): void {
  for (const entry of entries) {
    if (entry.n) {
      into[entry.p] = entry.n;
      continue;
    }
    into[entry.p] ??= null;
  }
}

/** The arena the room described, in the shape the renderer already consumes. */
export function pvpTeamArena(
  welcome: PvpTeamWelcomeFrame,
  appearance: Record<string, PvpAppearance | null>,
  /* Os nomes, da mesma lista de que a aparência sai (gate V28H.R). */
  nickname: Record<string, string | null>,
): PvpArena {
  return {
    width: welcome.arena.w,
    height: welcome.arena.h,
    obstacles: welcome.arena.obstacles,
    shipRadius: welcome.shipRadius,
    projectileRadius: welcome.projectileRadius,
    appearance,
    nickname,
    /*
     * Keyed by participant id, from the ROSTER - the room's statement of what
     * each of the six hulls is. Rebuilt into a lookup rather than passed as an
     * array, because everything downstream asks "what is a1's ceiling" and not
     * "what is the fourth entry".
     */
    combatProfile: Object.fromEntries(welcome.roster.map((entry) => [entry.p, entry.cp])),
    maxHealthMilli: welcome.maxHealthMilli,
    tickMs: welcome.tickMs,
    timeLimitMs: welcome.timeLimitMs,
    reconnectGraceMs: welcome.reconnectGraceMs,
  };
}

/* -------------------------------------------------------------------------- */

/** Partilhada e congelada: uma drenagem vazia nao aloca. */
const NO_TEAM_PROCS: readonly PvpTeamProcFrame[] = Object.freeze([]);

/** A gémea das explosões (gate V28G.J). */
const NO_TEAM_BURSTS: readonly PvpParticipantBleedBurst[] = Object.freeze([]);

/** Partilhada e congelada: um quadro sem reflexões não aloca (gate V28G.O). */
const NO_TEAM_REFLECTS: readonly PvpParticipantReflect[] = Object.freeze([]);
const NO_TEAM_BLASTS: readonly PvpTeamBlastFrame[] = Object.freeze([]);

export class PvpTeamConnection {
  private state: PvpTeamClientState = EMPTY;
  private readonly listeners = new Set<(state: PvpTeamClientState) => void>();

  private socket: PvpSocket | null = null;
  /**
   * Which attempt owns the socket.
   *
   * Every `connect` bumps it, and every handler captures the value it was
   * created under. A frame, a close or a resolved ticket belonging to a replaced
   * attempt is DROPPED - the same rule the room applies to a superseded chair.
   */
  private generation = 0;
  private nextSeq = 1;
  /** Set the moment the socket drops, so the grace window is measured honestly. */
  private droppedAt: number | null = null;
  private cancelRetry: (() => void) | null = null;
  private readyDeclared = false;
  /** See {@link pvpTeamFillAppearance}. One record per connection, filled in place. */
  private appearance: Record<string, PvpAppearance | null> = {};
  /** Os nomes, ao lado da arte e limpos com ela (gate V28H.R). */
  private nickname: Record<string, string | null> = {};

  constructor(private readonly deps: PvpTeamConnectionDeps) {}

  get snapshot(): PvpTeamClientState {
    return this.state;
  }

  subscribe(listener: (state: PvpTeamClientState) => void): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Opens the socket for the frozen chair this account already holds.
   *
   * No match id, because none is sent: the capability is minted from the
   * database against the session, and the room the socket reaches is the one
   * inside the capability. A client that could name a match here would be a
   * client that could ask about one it is not in.
   */
  connect(): void {
    this.generation += 1;
    this.readyDeclared = false;
    this.droppedAt = null;
    /* Ver `PvpConnection.connect`: a memoria de deduplicacao morre com a sala. */
    this.seenProcs.clear();
    this.pendingProcs = [];
    this.seenBlasts.clear();
    this.pendingBlasts = [];
    this.seenBursts.clear();
    this.pendingBursts = [];
    this.teardownSocket();
    this.appearance = {};
    this.nickname = {};
    this.patchWorld({
      phase: 'authorising',
      failure: null,
      /* Três espaços vazios: este fixture não é sobre cartas (V28H.J). */
      cards: emptyPvpCardSlots(),
      failureDetail: null,
    });
    void this.authoriseAndOpen(this.generation);
  }

  /**
   * Says this client has finished loading and is ready to fight.
   *
   * The room does not start until every chair has said it, and it is a frame the
   * protocol already carries - not something invented here to give a button
   * somewhere to point at. Repeated safely: the room takes the first and ignores
   * the rest, and a reconnecting client says it again because the room has no way
   * to know the tab is the same one.
   */
  declareReady(): void {
    this.readyDeclared = true;
    this.transmit({ t: 'ready' });
  }

  /**
   * Sends the player's current intent.
   *
   * One command, one sequence number, no retry and no queue - identical to the
   * duel, and against the same client schema, which the team protocol reuses
   * unchanged. If the socket is not open the intent is DROPPED rather than
   * buffered: a buffer would deliver a burst of stale directions the moment a
   * connection came back.
   */
  sendIntent(intent: PvpIntent): number | null {
    if (this.state.world.phase !== 'live' || this.state.world.matchState !== 'running') {
      return null;
    }
    const seq = this.nextSeq;
    this.nextSeq += 1;
    this.transmit({
      t: 'cmd',
      seq,
      mx: intent.mx,
      my: intent.my,
      ax: Math.trunc(intent.ax),
      ay: Math.trunc(intent.ay),
      f: intent.firing,
      ...(intent.teleportTo
        ? { tp: { x: Math.trunc(intent.teleportTo.x), y: Math.trunc(intent.teleportTo.y) } }
        : {}),
      /* A ordem de DETONAR, e só quando ela existe (gate V28G.N). */
      ...(intent.detonateBleed === true ? { bd: true as const } : {}),
      /* E a ORDEM da carta, só quando ela existe (gate V28H.J). */
      ...(intent.activateCard
        ? { ck: { s: intent.activateCard.slot, id: intent.activateCard.commandId } }
        : {}),
    });
    return seq;
  }

  /**
   * Tells the room this chair has chosen to abandon the match (ADR 0043).
   *
   * The same bare `{ t: 'leave' }` the duel sends, against the same client
   * schema, which this protocol reuses unchanged. It carries no participant, no
   * team and no match: the room derives WHO from the socket the frame arrived on,
   * which a signed one-use ticket bound to exactly one chair. There is no field
   * in which to name somebody else, and `.strict()` keeps that true.
   *
   * It tears nothing down. The socket stays open precisely so the room's answer
   * can arrive on it - and in a 3x3 that answer ends the match for all six, in
   * favour of the other team, because there is no 2x3 contract to continue into.
   *
   * Answering `false` means the intent could not be sent at all, which the caller
   * has to show as a failure rather than hide as an exit.
   */
  requestLeave(): boolean {
    if (this.state.world.phase !== 'live') {
      return false;
    }
    const matchState = this.state.world.matchState;
    if (matchState !== 'running' && matchState !== 'ready') {
      return false;
    }
    return this.transmit({ t: 'leave' });
  }

  /** Lets go for good. Not a disconnection the room should wait out. */
  dispose(): void {
    this.generation += 1;
    this.cancelRetry?.();
    this.cancelRetry = null;
    this.teardownSocket();
  }

  /* ---------------------------------------------------------------------- */

  private async authoriseAndOpen(generation: number): Promise<void> {
    let ticket: string;
    try {
      // A FRESH capability every time. The one from last connection is spent,
      // and presenting it again is refused by the room by design.
      ticket = (await this.deps.mintTicket()).ticket;
    } catch (error) {
      if (generation !== this.generation) {
        return;
      }
      this.fail('not_authorised', reasonOf(error));
      return;
    }
    if (generation !== this.generation) {
      // The caller moved on while the request was in flight. The capability
      // simply expires unused; nothing is opened on its behalf.
      return;
    }

    this.patchWorld({ phase: 'connecting' });
    const socket = this.deps.openSocket(this.deps.socketUrl(ticket));
    this.socket = socket;

    socket.onOpen(() => {
      if (generation !== this.generation) {
        return;
      }
      // Nothing is sent here. The room speaks first, with a welcome, and the
      // sequence this client may use comes out of it.
      this.droppedAt = null;
    });
    socket.onMessage((data) => {
      if (generation !== this.generation) {
        return;
      }
      this.receive(data);
    });
    socket.onError(() => {
      if (generation !== this.generation) {
        return;
      }
      this.handleDrop(generation);
    });
    socket.onClose(() => {
      if (generation !== this.generation) {
        return;
      }
      this.handleDrop(generation);
    });
  }

  /**
   * Reads one frame, or ignores it.
   *
   * Parsed against the TEAM union rather than trusted, and a frame this build
   * does not understand is dropped silently - including a duel frame, which this
   * socket can never legitimately receive and which would otherwise be read by
   * whichever branch happened to match first.
   */
  private receive(data: string): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      return;
    }
    const frame = pvpTeamServerFrameSchema.safeParse(parsed);
    if (!frame.success) {
      return;
    }

    switch (frame.data.t) {
      case 'welcome':
        this.applyWelcome(frame.data);
        return;
      case 'state':
        this.applyState(frame.data);
        return;
      case 'ended':
        /*
         * The ONLY place a result is ever set.
         *
         * Not a hull at zero health, not the clock, not a closing socket, and
         * not "the last team with a ship on screen". A client that announced its
         * own result would announce a different one from the server's the first
         * time a frame went missing.
         */
        this.patch({
          world: {
            ...this.state.world,
            phase: 'ended',
            matchState: frame.data.state,
          },
          result: frame.data,
        });
        return;
      case 'error':
        this.patchWorld({ failureDetail: frame.data.reason });
        return;
      case 'closing':
        this.applyClosing(frame.data.reason);
        return;
    }
  }

  private applyWelcome(welcome: PvpTeamWelcomeFrame): void {
    if (welcome.rulesVersion !== this.deps.rulesVersion) {
      /*
       * The server is running numbers this bundle does not have. Refused rather
       * than rendered, exactly as the duel refuses it: a match drawn with one
       * set of speeds and decided with another is a match the losing team cannot
       * interpret.
       */
      this.fail('rules_version_mismatch', `server=${welcome.rulesVersion}`);
      return;
    }

    /*
     * The next sequence, taken from the server on EVERY welcome.
     *
     * First connection, F5, reconnection, a generation replaced by another tab:
     * all four arrive here, and all four resume from what the ship has actually
     * applied. The applied sequence belongs to the CHAIR and survives a
     * reconnection, so counting from one again would have every command dropped
     * as a replay - silently, and only while reconnecting.
     */
    this.nextSeq = welcome.you.ack + 1;
    this.droppedAt = null;
    pvpTeamFillAppearance(this.appearance, welcome.roster);
    pvpTeamFillNicknames(this.nickname, welcome.roster);

    this.patch({
      world: {
        ...this.state.world,
        phase: 'live',
        matchId: welcome.matchId,
        matchState: welcome.state,
        ack: welcome.you.ack,
        arena: pvpTeamArena(welcome, this.appearance, this.nickname),
        // A welcome is a discontinuity by definition: the world it describes may
        // be a second behind the one on screen, or a whole match ahead of it.
        correction: this.state.world.correction + 1,
        failure: null,
      },
      you: welcome.you,
      roster: welcome.roster,
    });

    if (this.readyDeclared) {
      // A reconnecting client says it again. The room cannot know this tab is
      // the same one, and a `ready` it already has costs it a boolean.
      this.transmit({ t: 'ready' });
    }
  }

  /**
   * Os cortes confirmados que ainda ninguém consumiu, e os já vistos.
   *
   * Idêntico a `PvpConnection`, e deliberadamente idêntico: os dois modos
   * entregam o mesmo facto, e uma apresentação partilhada tem de o drenar da
   * mesma maneira em ambos. Uma fila, e não um campo do frame de render - esse é
   * uma mistura de duas instantâneas e é reconstruído a cada quadro desenhado,
   * portanto um corte que vivesse lá seria redesenhado sessenta vezes por
   * segundo.
   */
  private pendingProcs: PvpTeamProcFrame[] = [];
  /**
   * Os procs já aceites, por `(projétil, EFEITO, ALVO)` (gate V28G.U).
   *
   * A mesma chave da ligação do duelo, e a razão pela qual ela tem três partes
   * pesa MAIS aqui: um 3x3 tem seis cascos, portanto é neste modo que o mesmo
   * efeito do mesmo payload pode ser confirmado sobre alvos diferentes - e uma
   * chave que fosse só o id do projétil desenharia um deles e engoliria os
   * outros em silêncio.
   *
   * Era `Set<number>` e era o id sozinho. Duas coisas cabiam nessa lacuna: dois
   * EFEITOS do mesmo tiro - um corte do Ciclone e o sifão do Orbe Vampírico
   * anunciam-se os dois com o mesmo `i` - e o mesmo efeito em dois ALVOS. O
   * Coração Vital tornou a primeira inevitável, porque a cura é anunciada no
   * disparo com o id do projétil que a salva acabou de criar, e esse projétil
   * ainda vai cortar quando aterrar.
   *
   * O trio continua a ser único por construção, e por isso continua a recusar
   * exatamente o que tem de recusar: uma REPETIÇÃO. Um frame reenviado ou um
   * tique re-simulado trazem os três campos iguais.
   */
  private readonly seenProcs = new Set<string>();

  /** A fila e a memória das explosões. As gémeas exatas (gate V28G.J). */
  private pendingBlasts: PvpTeamBlastFrame[] = [];
  private readonly seenBlasts = new Set<number>();

  private acceptBlasts(blasts: readonly PvpTeamBlastFrame[]): void {
    for (const blast of blasts) {
      if (this.seenBlasts.has(blast.i)) {
        continue;
      }
      this.seenBlasts.add(blast.i);
      this.pendingBlasts.push(blast);
    }
  }

  /** Entrega as explosões confirmadas desde a última chamada, e esvazia a fila. */
  drainBlasts(): readonly PvpTeamBlastFrame[] {
    if (this.pendingBlasts.length === 0) {
      return NO_TEAM_BLASTS;
    }
    const taken = this.pendingBlasts;
    this.pendingBlasts = [];
    return taken;
  }

  /**
   * A fila e a memória das DETONAÇÕES (gate V28G.N).
   *
   * Idênticas às do duelo, com a chave `(autor, tique)`: uma detonação não tem
   * projétil, portanto a identidade é quem mandou e quando.
   */
  private pendingBursts: PvpParticipantBleedBurst[] = [];
  private readonly seenBursts = new Set<string>();

  private acceptBursts(bursts: readonly PvpTeamBleedBurstFrame[]): void {
    for (const burst of bursts) {
      const key = burst.p + String(burst.t);
      if (this.seenBursts.has(key)) {
        continue;
      }
      this.seenBursts.add(key);
      for (const target of burst.q) {
        this.pendingBursts.push({
          key: key + target.s,
          p: burst.p,
          q: target.s,
          n: target.n,
          d: BigInt(target.d),
        });
      }
    }
  }

  /** Entrega as detonações desde a última chamada, e esvazia a fila. */
  drainBleedBursts(): readonly PvpParticipantBleedBurst[] {
    if (this.pendingBursts.length === 0) {
      return NO_TEAM_BURSTS;
    }
    const taken = this.pendingBursts;
    this.pendingBursts = [];
    return taken;
  }

  /**
   * A fila e a memória das REFLEXÕES (gate V28G.O).
   *
   * Idênticas às do duelo, com a mesma chave: uma reflexão não tem projétil, e
   * `(autor, tique)` não chega - no mesmo tique um casco pode intercetar um tiro
   * e uma parcela de veneno do mesmo adversário.
   */
  private pendingReflects: PvpParticipantReflect[] = [];
  private readonly seenReflects = new Set<string>();

  private acceptReflects(reflects: readonly PvpTeamReflectFrame[]): void {
    for (const reflect of reflects) {
      const key = `${reflect.p}:${reflect.q}:${reflect.t}:${reflect.k}:${reflect.s}`;
      if (this.seenReflects.has(key)) {
        continue;
      }
      this.seenReflects.add(key);
      this.pendingReflects.push({
        key,
        p: reflect.p,
        q: reflect.q,
        k: reflect.k,
        s: reflect.s,
        x: reflect.x,
        y: reflect.y,
        tx: reflect.tx,
        ty: reflect.ty,
        d: reflect.d === undefined ? null : BigInt(reflect.d),
      });
    }
  }

  /** Entrega as reflexões desde a última chamada, e esvazia a fila. */
  drainReflects(): readonly PvpParticipantReflect[] {
    if (this.pendingReflects.length === 0) {
      return NO_TEAM_REFLECTS;
    }
    const taken = this.pendingReflects;
    this.pendingReflects = [];
    return taken;
  }

  private acceptProcs(procs: readonly PvpTeamProcFrame[]): void {
    for (const proc of procs) {
      const key = pvpProcDedupKey(proc);
      if (this.seenProcs.has(key)) {
        continue;
      }
      this.seenProcs.add(key);
      this.pendingProcs.push(proc);
    }
  }

  /** Entrega os cortes desde a última chamada, e esvazia a fila. */
  drainProcs(): readonly PvpTeamProcFrame[] {
    if (this.pendingProcs.length === 0) {
      return NO_TEAM_PROCS;
    }
    const taken = this.pendingProcs;
    this.pendingProcs = [];
    return taken;
  }

  private applyState(frame: PvpTeamStateFrame): void {
    /*
     * A tick that went backwards is a REBUILD, not an error.
     *
     * The room persists a running match about once a second, so a rebuilt object
     * resumes the last durable tick and re-simulates from there. The client's job
     * is to accept it and to tell the renderer not to slide anything across the
     * seam.
     */
    const rewound = frame.k < this.state.world.tick;
    this.acceptProcs(frame.procs);
    this.acceptBlasts(frame.blasts);
    this.acceptBursts(frame.bdt);
    this.acceptReflects(frame.rfl);
    pvpTeamFillAppearance(this.appearance, frame.parts);
    pvpTeamFillNicknames(this.nickname, frame.parts);
    this.patch({
      world: {
        ...this.state.world,
        matchState: frame.state,
        tick: frame.k,
        ack: frame.you.ack,
        // Wholesale, all three. A projectile the server did not send is gone,
        // and merging would be this client inventing one.
        ships: frame.parts.map(pvpTeamShip),
        /*
         * **E as INVOCAÇÕES** (gate V28H.S), pela mesma regra de tudo o resto:
         * uma que sumiu do frame acabou.
         *
         * Faltavam, e é por isso que o drone do 3x3 disparava sem existir: os
         * tiros dele são projéteis e viajavam na lista deles, e o CORPO parava
         * aqui. O duelo já as carregava - esta é a mesma omissão que `cv` teve,
         * no mesmo mapeamento.
         */
        summons: frame.sm ?? [],
        projectiles: frame.proj.map(pvpTeamProjectile),
        missiles: frame.mis.map(pvpTeamMissile),
        effects: frame.parts.map(pvpTeamEffects),
        runes: pvpTeamRunes(frame.runes),
        /* E os stacks pela mesma regra: um que sumiu do frame expirou ou morreu. */
        poison: frame.psn.map(pvpTeamPoison),
        /* E as marcas pela mesma regra: uma que sumiu rebentou ou morreu. */
        bleeding: frame.bld.map(pvpTeamBleed),
        msLeft: frame.left,
        correction: rewound ? this.state.world.correction + 1 : this.state.world.correction,
      },
      you: frame.you,
    });
  }

  private applyClosing(reason: PvpCloseReason): void {
    if (reason === 'superseded') {
      /*
       * Another tab took this chair. NOT a reconnection to attempt: the player
       * is still there, they just moved tab, and a client that fought its way
       * back would produce two tabs taking the chair from each other.
       */
      this.fail('superseded', null);
      return;
    }
    if (reason === 'match_over') {
      // The room is letting go. If the ending already arrived, keep it: a socket
      // closing must never overwrite the result it delivered.
      this.generation += 1;
      this.cancelRetry?.();
      this.cancelRetry = null;
      this.teardownSocket();
      if (this.state.result === null) {
        this.patchWorld({ phase: 'closed', failure: 'match_over' });
      } else {
        this.patchWorld({ phase: 'ended' });
      }
      return;
    }
    // unauthorised, rate_limited, malformed, pvp_disabled: this client is the
    // problem, and repeating it would only repeat the refusal.
    this.fail(reason, null);
  }

  /**
   * The socket went away without the room saying why.
   *
   * A network blip, a laptop lid, a proxy. The room holds the chair for its
   * grace window, so the honest answer is to mint a NEW capability and come back
   * - and to stop once that window has passed rather than hammering a room that
   * has already given the win away.
   */
  private handleDrop(generation: number): void {
    if (generation !== this.generation) {
      return;
    }
    this.teardownSocket();
    if (this.state.world.phase === 'ended' || this.state.world.phase === 'closed') {
      return;
    }

    const now = this.deps.now();
    this.droppedAt ??= now;
    const grace = this.state.world.arena?.reconnectGraceMs ?? 0;
    if (now - this.droppedAt >= grace) {
      /*
       * The window measured from when we actually dropped, on this client's
       * clock, and only as a courtesy: the SERVER's grace window is the one that
       * decides the match. Giving up here simply stops asking.
       */
      this.fail('reconnect_failed', null);
      return;
    }

    this.patchWorld({ phase: 'connecting' });
    const droppedAt = this.droppedAt;
    this.cancelRetry = this.deps.schedule(() => {
      this.cancelRetry = null;
      if (generation !== this.generation) {
        return;
      }
      // A new attempt, and therefore a new capability. The spent one could not
      // reopen anything even if this client kept it.
      this.generation += 1;
      this.droppedAt = droppedAt;
      void this.authoriseAndOpen(this.generation);
    }, RECONNECT_DELAY_MS);
  }

  /**
   * Writes one frame, and says whether it went.
   *
   * The answer matters for exactly one caller: {@link requestLeave}, which must
   * not let a player believe they abandoned a match the room never heard about.
   * Every other caller ignores it, because a dropped intent is already the
   * documented behaviour there.
   */
  private transmit(frame: unknown): boolean {
    const socket = this.socket;
    if (!socket) {
      return false;
    }
    try {
      socket.send(JSON.stringify(frame));
      return true;
    } catch {
      // A socket that cannot be written to is a socket that is already gone;
      // its close handler does the rest.
      return false;
    }
  }

  private teardownSocket(): void {
    const socket = this.socket;
    this.socket = null;
    if (!socket) {
      return;
    }
    try {
      socket.close();
    } catch {
      /* Already gone. */
    }
  }

  private fail(failure: PvpClientFailure, detail: string | null): void {
    this.generation += 1;
    this.cancelRetry?.();
    this.cancelRetry = null;
    this.teardownSocket();
    this.patchWorld({ phase: 'closed', failure, failureDetail: detail });
  }

  /** Changes only the world, which is most of what arrives. */
  private patchWorld(change: Partial<PvpClientState>): void {
    this.patch({ world: { ...this.state.world, ...change } });
  }

  private patch(change: Partial<PvpTeamClientState>): void {
    this.state = { ...this.state, ...change };
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }
}

function reasonOf(error: unknown): string | null {
  const reason = (error as { reason?: unknown } | null)?.reason;
  return typeof reason === 'string' ? reason : null;
}
